import dayjs from 'dayjs';
import { createChart, IChartApi, ColorType } from 'lightweight-charts';
import React, { useEffect, useRef, useState } from 'react';
import leverageUiColors from '../../helpers/config/colors';
import "./LWChart.scss";
import { formatCurrencyAmount } from '../../helpers/formatters';

interface ILWChartComponentProps {
    data: { time: string; value: number }[];
    accentColor?: string;
}

interface ITooltipContent {
    display: boolean;
    left: number;
    content: string;
}

function hexToRGBA(hex: string, opacity: number): string {
    hex = hex.replace('#', '');

    let r = 0, g = 0, b = 0;
    if (hex.length === 3) {
        r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
        g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
        b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
    }

    // Return the RGBA color string
    return `rgba(${r},${g},${b},${opacity})`;
}

export const LWChart: React.FC<ILWChartComponentProps> = ({
                                                              data,
                                                              accentColor = leverageUiColors.main
                                                          }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [tooltipContent, setTooltipContent] = useState<ITooltipContent>({ display: false, left: 0, content: '' });

    const toolipWidth = 96;
    const halfTooltipWidth = toolipWidth / 2;

    useEffect(() => {
        if (chartContainerRef.current) {
            const handleResize = () => {
                chart.applyOptions({ width: chartContainerRef.current!.clientWidth });
            };

            const chart: IChartApi = createChart(chartContainerRef.current, {
                width: chartContainerRef.current.clientWidth,
                height: 300,
                layout: {
                    textColor: '#fff',
                    background: {
                        type: ColorType.Solid,
                        color: leverageUiColors.bg2
                    }
                },
                rightPriceScale: {
                    borderVisible: false,
                    autoScale: true
                },
                crosshair: {
                    vertLine: {
                        width: 1,
                        visible: true,
                    },
                    horzLine: {
                        visible: true,
                        labelVisible: false
                    }
                },
                grid: {
                    vertLines: {
                        color: 'transparent',
                        visible: false
                    },
                    horzLines: {
                        color: 'transparent',
                        visible: false
                    }
                },
                timeScale: {
                    borderVisible: false,
                    fixLeftEdge: true,
                    fixRightEdge: true,
                    lockVisibleTimeRangeOnResize: true,
                    rightOffset: 0,
                    tickMarkFormatter: (time: string) => {
                        return dayjs(time).format('D MMM')
                    }
                }
            });
            chart.timeScale().fitContent();


            const newSeries = chart.addAreaSeries({
                topColor: hexToRGBA(accentColor, 0.4),
                bottomColor: hexToRGBA(accentColor, 0.04),
                lineColor: accentColor,
                lineWidth: 1,
                priceFormat: {
                    type: 'custom',
                    formatter: (price: number) => Number(price).toFixed(0),
                },
            });
            newSeries.setData(data);

            chart.subscribeCrosshairMove(param => {
                if (
                    param.point === undefined ||
                    !param.time ||
                    param.point.x < 0 ||
                    param.point.x > (chartContainerRef?.current?.clientWidth || 0) ||
                    param.point.y < 0 ||
                    param.point.y > (chartContainerRef?.current?.clientHeight || 0)
                ) {
                    setTooltipContent(prev => ({ ...prev, display: false }));
                } else {
                    const seriesData = param.seriesData.get(newSeries);
                    const priceScaleWidth = chart.priceScale('left').width();
                    let left = param.point.x - halfTooltipWidth;
                    left = Math.max(left, priceScaleWidth);

                    setTooltipContent({
                        display: true,
                        left,
                        content: `${formatCurrencyAmount((seriesData as any)?.value, '', 0)}`
                    });
                }
            });

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                chart.remove();
            };
        }
    }, [data, accentColor]);

    return <div className="LWChart" ref={chartContainerRef}>{tooltipContent.display &&
        <div className="LWChart__tooltip"
             style={{
                 left: `${tooltipContent.left}px`,
                 display: 'block'
             }} dangerouslySetInnerHTML={{ __html: tooltipContent.content }}></div>}
    </div>;
};



